/**
 * Target Ranges Form Component
 *
 * Allows users to configure target ranges for key water parameters.
 * Values outside these ranges will be highlighted on the dashboard.
 */

import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Title,
  Text,
  Stack,
  Group,
  NumberInput,
  Switch,
  Button,
  Table,
  Alert,
  LoadingOverlay,
} from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useTank } from '../contexts/TankContext';
import { NoTankSelected } from './common/NoTankSelected';
import { api } from '../api/client';
import { tankTargetRangesQueryKey } from '../api/measurements';
import { TargetRange, DEFAULT_PARAMETERS } from '../types/settings';

/**
 * Returns the unit suffix to append after a parameter's display name, or an
 * empty string when the name already ends with the unit (e.g. "Alkalinity
 * (dKH)" with unit "dKH" would otherwise render as "Alkalinity (dKH) (dKH)").
 */
export function parameterUnitSuffix(parameterName: string, unit: string): string {
  if (!unit) return '';
  if (parameterName.trimEnd().endsWith(`(${unit})`)) return '';
  return unit;
}

export const TargetRangesForm: React.FC = () => {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();
  const [ranges, setRanges] = useState<TargetRange[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Load existing target ranges
  useEffect(() => {
    const loadRanges = async () => {
      if (!selectedTank) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await api.get<{ ranges: TargetRange[] }>(
          `/api/tanks/${selectedTank.id}/target-ranges`
        );
        setRanges(response.ranges);
      } catch (err) {
        // If no ranges exist, initialize with defaults
        setRanges(
          DEFAULT_PARAMETERS.map((p) => ({
            parameterId: p.id,
            parameterName: p.name,
            unit: p.unit,
            minValue: p.defaultMin,
            maxValue: p.defaultMax,
            enabled: true,
          }))
        );
      } finally {
        setLoading(false);
      }
    };
    loadRanges();
  }, [selectedTank]);

  const updateRange = (
    parameterId: string,
    field: keyof TargetRange,
    value: number | boolean | null
  ) => {
    setRanges((prev) =>
      prev.map((r) => (r.parameterId === parameterId ? { ...r, [field]: value } : r))
    );
    setSuccess(false);
  };

  const handleSave = async () => {
    if (!selectedTank) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await api.put(`/api/tanks/${selectedTank.id}/target-ranges`, {
        ranges: ranges,
      });
      // Charts, tiles and the manual entry modal read cached ranges; refresh
      // them so the new values show without waiting for the cache to expire.
      queryClient.invalidateQueries({ queryKey: tankTargetRangesQueryKey(selectedTank.id) });
      queryClient.invalidateQueries({ queryKey: ['dashboard', selectedTank.id] });
      setSuccess(true);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to save target ranges';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (!selectedTank) {
    return (
      <NoTankSelected purpose="to configure target ranges" />
    );
  }

  return (
    <Card withBorder p="lg">
      <LoadingOverlay visible={loading} />

      <Stack>
        <div>
          <Title order={4}>Target Ranges</Title>
          <Text size="sm" c="dimmed">
            Configure target ranges for key parameters. Values outside these ranges will
            be highlighted on the dashboard.
          </Text>
        </div>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {error}
          </Alert>
        )}

        {success && (
          <Alert icon={<IconCheck size={16} />} color="green">
            Target ranges saved successfully.
          </Alert>
        )}

        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Parameter</Table.Th>
              <Table.Th>Min</Table.Th>
              <Table.Th>Max</Table.Th>
              <Table.Th>Enabled</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {ranges.map((range) => (
              <Table.Tr key={range.parameterId}>
                <Table.Td>
                  {range.parameterName}
                  {parameterUnitSuffix(range.parameterName, range.unit) && (
                    <Text span size="xs" c="dimmed">
                      {' '}
                      ({parameterUnitSuffix(range.parameterName, range.unit)})
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    size="xs"
                    w={80}
                    value={range.minValue ?? ''}
                    onChange={(val) =>
                      updateRange(range.parameterId, 'minValue', val as number | null)
                    }
                    disabled={!range.enabled}
                    decimalScale={2}
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    size="xs"
                    w={80}
                    value={range.maxValue ?? ''}
                    onChange={(val) =>
                      updateRange(range.parameterId, 'maxValue', val as number | null)
                    }
                    disabled={!range.enabled}
                    decimalScale={2}
                  />
                </Table.Td>
                <Table.Td>
                  <Switch
                    checked={range.enabled}
                    onChange={(e) =>
                      updateRange(range.parameterId, 'enabled', e.target.checked)
                    }
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        <Group justify="flex-end">
          <Button onClick={handleSave} loading={saving}>
            Save Target Ranges
          </Button>
        </Group>
      </Stack>
    </Card>
  );
};
